// Polymarket trading dashboard API client
// Typed fetch wrappers for all backend endpoints (port 8085)

import type {
  HealthResponse,
  StatusResponse,
  TradesResponse,
  PositionsResponse,
  BalanceResponse,
  StatsResponse,
  ConfigResponse,
  KillSwitchInfo,
  KillSwitchActivateResponse,
  KillSwitchDeactivateResponse,
  PricesResponse,
  DbInfoResponse,
} from './types';

// Empty string = same origin (dashboard proxies to backend in dev via Vite config)
const BASE = '';

async function fetchJson<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, init);
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
}

// GET /api/health
export async function getHealth(): Promise<HealthResponse> {
  return fetchJson('/api/health');
}

// GET /api/status
export async function getStatus(): Promise<StatusResponse> {
  return fetchJson('/api/status');
}

// GET /api/trades?limit=N
export async function getTrades(limit = 50): Promise<TradesResponse> {
  return fetchJson(`/api/trades?limit=${limit}`);
}

// GET /api/positions
export async function getPositions(): Promise<PositionsResponse> {
  return fetchJson('/api/positions');
}

// GET /api/balance
export async function getBalance(): Promise<BalanceResponse> {
  return fetchJson('/api/balance');
}

// GET /api/stats
export async function getStats(): Promise<StatsResponse> {
  return fetchJson('/api/stats');
}

// GET /api/config
export async function getConfig(): Promise<ConfigResponse> {
  return fetchJson('/api/config');
}

// GET /api/kill-switch
export async function getKillSwitch(): Promise<KillSwitchInfo> {
  return fetchJson('/api/kill-switch');
}

// POST /api/kill-switch/activate
export async function activateKillSwitch(
  reason = 'dashboard manual activation',
): Promise<KillSwitchActivateResponse> {
  return fetchJson('/api/kill-switch/activate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
}

// POST /api/kill-switch/deactivate
export async function deactivateKillSwitch(): Promise<KillSwitchDeactivateResponse> {
  return fetchJson('/api/kill-switch/deactivate', { method: 'POST' });
}

// GET /api/prices?limit=N&token_id=X
export async function getPrices(
  limit = 100,
  tokenId?: string,
): Promise<PricesResponse> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (tokenId) params.set('token_id', tokenId);
  return fetchJson(`/api/prices?${params}`);
}

// GET /api/db
export async function getDbInfo(): Promise<DbInfoResponse> {
  return fetchJson('/api/db');
}

// GET /api/log/stream (Server-Sent Events)
export function subscribeLogStream(
  onMessage: (line: string) => void,
  onError?: (err: Event) => void,
): EventSource {
  const source = new EventSource(`${BASE}/api/log/stream`);
  source.onmessage = (event) => onMessage(event.data);
  if (onError) source.onerror = onError;
  return source;
}
